/**
 * Product page variant buttons ("Maak een keuze") in one horizontally scrolling row with our
 * own scrollbar: round prev/next buttons around a grey track with a draggable red thumb.
 * The theme swaps the <ul class="check box"> wholesale on a variant change (custom-async.js,
 * replaceChild by id), so the wrapper is ours and the list inside it is looked up every time.
 */
// .m6pr-compact is the same section loaded into the quick view panel (custom-async.js, quickShop).
const LIST = ':is(.m6pr, .m6pr-compact) .f8pr-variant-selection ul.check.box';

const chevron = (dir) =>
  `<svg viewBox="0 0 16 16" aria-hidden="true"><path d="${
    dir === 'prev' ? 'M10 3 5 8l5 5' : 'M6 3l5 5-5 5'
  }" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

function enhance(list) {
  const wrap = document.createElement('div');
  wrap.className = 'bx-variant-scroll';
  list.parentNode.insertBefore(wrap, list);
  wrap.appendChild(list);

  const bar = document.createElement('div');
  bar.className = 'bx-variant-scroll__bar';
  bar.innerHTML = `
    <button type="button" class="bx-variant-scroll__btn" data-dir="-1" aria-label="Vorige keuzes">${chevron('prev')}</button>
    <div class="bx-variant-scroll__track"><span class="bx-variant-scroll__thumb">${chevron('prev')}${chevron('next')}</span></div>
    <button type="button" class="bx-variant-scroll__btn" data-dir="1" aria-label="Volgende keuzes">${chevron('next')}</button>`;
  wrap.appendChild(bar);

  const track = bar.querySelector('.bx-variant-scroll__track');
  const thumb = bar.querySelector('.bx-variant-scroll__thumb');
  const [prev, next] = bar.querySelectorAll('.bx-variant-scroll__btn');
  const getList = () => wrap.querySelector('ul.check');
  let scrollLeft = 0;

  const update = () => {
    const ul = getList();
    if (!ul) return;
    const max = ul.scrollWidth - ul.clientWidth;
    wrap.classList.toggle('bx-variant-scroll--active', max > 1);
    if (max <= 1) return;
    const trackWidth = track.clientWidth;
    const thumbWidth = Math.max(56, (trackWidth * ul.clientWidth) / ul.scrollWidth);
    const ratio = Math.min(1, Math.max(0, ul.scrollLeft / max));
    thumb.style.width = `${thumbWidth}px`;
    thumb.style.transform = `translateX(${(trackWidth - thumbWidth) * ratio}px)`;
    prev.disabled = ul.scrollLeft <= 1;
    next.disabled = ul.scrollLeft >= max - 1;
    wrap.classList.toggle('bx-variant-scroll--start', prev.disabled);
    wrap.classList.toggle('bx-variant-scroll--end', next.disabled);
  };

  // Scroll events don't bubble, but they do pass ancestors in the capture phase.
  wrap.addEventListener(
    'scroll',
    () => {
      scrollLeft = getList()?.scrollLeft || 0;
      update();
    },
    true,
  );

  bar.addEventListener('click', (event) => {
    const btn = event.target.closest('.bx-variant-scroll__btn');
    const ul = getList();
    if (!btn || !ul) return;
    ul.scrollBy({ left: Number(btn.dataset.dir) * ul.clientWidth * 0.8, behavior: 'smooth' });
  });

  // Click on the track jumps there; dragging the thumb scrolls along.
  track.addEventListener('pointerdown', (event) => {
    const ul = getList();
    if (!ul) return;
    event.preventDefault();
    const max = ul.scrollWidth - ul.clientWidth;
    const room = track.clientWidth - thumb.offsetWidth;
    if (room <= 0) return;
    if (event.target.closest('.bx-variant-scroll__thumb') === null) {
      const x = event.clientX - track.getBoundingClientRect().left - thumb.offsetWidth / 2;
      ul.scrollLeft = (x / room) * max;
    }
    const startX = event.clientX;
    const startScroll = ul.scrollLeft;
    track.setPointerCapture(event.pointerId);
    wrap.classList.add('bx-variant-scroll--dragging');
    const move = (e) => {
      ul.scrollLeft = startScroll + ((e.clientX - startX) / room) * max;
    };
    const end = () => {
      track.removeEventListener('pointermove', move);
      wrap.classList.remove('bx-variant-scroll--dragging');
    };
    track.addEventListener('pointermove', move);
    track.addEventListener('pointerup', end, { once: true });
    track.addEventListener('pointercancel', end, { once: true });
  });

  // Keep the scroll position when the theme swaps in a fresh list.
  new MutationObserver(() => {
    const ul = getList();
    if (!ul) return;
    ul.scrollLeft = scrollLeft;
    resize.observe(ul);
    update();
  }).observe(wrap, { childList: true });

  const resize = new ResizeObserver(update);
  resize.observe(wrap);
  resize.observe(list);

  // Start with the selected choice in view.
  const checked = list.querySelector('input:checked + label');
  if (checked) {
    const box = checked.getBoundingClientRect();
    const view = list.getBoundingClientRect();
    list.scrollLeft += box.left - view.left - (view.width - box.width) / 2;
  }
  update();
}

export function initVariantScroll(root = document) {
  root.querySelectorAll(LIST).forEach((list) => {
    if (!list.closest('.bx-variant-scroll')) enhance(list);
  });
}
